/**
 * Partner Edit Page
 * Partner adatok szerkesztése
 */

import { updatePartnerEditor } from '@/api/partner-editor';
import type { PartnerCategory, PartnerType } from '@/api/partners';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { usePartnerEditor } from '@/hooks/use-partner-editor';
import { useTranslation } from '@kgc/i18n';
import type { PartnerEditorRead } from '@kgc/partners/partner-editor';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhoneListEditor } from './components/PhoneListEditor';

const PARTNER_CATEGORY_COLORS: Record<string, string> = {
  retail: 'bg-blue-100 text-blue-800',
  wholesale: 'bg-purple-100 text-purple-800',
  rental: 'bg-green-100 text-green-800',
  service: 'bg-orange-100 text-orange-800',
  supplier: 'bg-red-100 text-red-800',
  contractor: 'bg-muted text-foreground',
};

const PARTNER_CATEGORY_VALUES: PartnerCategory[] = [
  'retail',
  'wholesale',
  'rental',
  'service',
  'supplier',
  'contractor',
];
const PARTNER_TYPE_VALUES: PartnerType[] = ['individual', 'company'];

interface FormData {
  type: PartnerType;
  categories: PartnerCategory[];
  name: string;
  shortName: string;
  taxNumber: string;
  registrationNumber: string;
  email: string;
  notificationEmail: string;
  billingEmail: string;
  billingEmailSame: boolean;
  phone: string;
  website: string;
  postalCode: string;
  city: string;
  street: string;
  building: string;
  // Temporary address (FG-1-4)
  temporaryAddress: string;
  tempAddressValidUntil: string;
  // Personal data (individuals)
  birthDate: string;
  birthPlace: string;
  mothersName: string;
  idCardNumber: string;
  idCardExpiry: string;
  lastDocumentScanDate: string;
  // Financial
  paymentTermDays: number;
  creditLimit: string;
  discountPercent: string;
  discountRental: string;
  discountSales: string;
  discountService: string;
  rentalDepositPercent: string;
  paymentBypassEnabled: boolean;
  idVerified: boolean;
  notes: string;
}

function partnerToFormData(partner: PartnerEditorRead): FormData {
  const billingAddress = partner.addresses.find(a => a.type.toLowerCase() === 'billing');
  return {
    type: partner.type,
    categories: partner.categories as PartnerCategory[],
    name: partner.name,
    shortName: '',
    taxNumber: partner.taxNumber ?? '',
    registrationNumber: '',
    email: partner.email ?? '',
    notificationEmail: partner.notificationEmail ?? '',
    billingEmail: partner.billingEmail ?? '',
    billingEmailSame: !partner.billingEmail || partner.billingEmail === partner.notificationEmail,
    phone: partner.phone ?? '',
    website: '',
    postalCode: billingAddress?.postalCode ?? '',
    city: billingAddress?.city ?? '',
    street: billingAddress?.street ?? '',
    building: '',
    temporaryAddress: partner.temporaryAddress ?? '',
    tempAddressValidUntil: partner.tempAddressValidUntil ?? '',
    birthDate: partner.birthDate ?? '',
    birthPlace: partner.birthPlace ?? '',
    mothersName: partner.mothersName ?? '',
    // #188 masked-ID: a tárolt idCardNumber maszkolt (a GET csak maskedIdCardNumber-t ad),
    // nyers nincs → NEM prefilljük; üresen hagyva a save kihagyja → BE megtartja a meglévőt.
    idCardNumber: '',
    idCardExpiry: partner.idCardExpiry ?? '',
    lastDocumentScanDate: partner.lastDocumentScanDate ?? '',
    paymentTermDays: partner.paymentTermDays ?? 8,
    creditLimit: partner.creditLimit?.toString() ?? '',
    discountPercent: partner.defaultDiscountPc?.toString() ?? '',
    discountRental: partner.discountRental?.toString() ?? '',
    discountSales: partner.discountSales?.toString() ?? '',
    discountService: partner.discountService?.toString() ?? '',
    rentalDepositPercent: '30',
    paymentBypassEnabled: partner.paymentBypassEnabled ?? false,
    idVerified: partner.idVerified ?? false,
    notes: partner.notes ?? '',
  };
}

export function PartnerEditPage() {
  const { t } = useTranslation();

  const PARTNER_CATEGORIES = PARTNER_CATEGORY_VALUES.map(value => ({
    value,
    label: t(
      `partner.categoryLabels.${value === 'rental' ? 'renter' : value === 'contractor' ? 'subcontractor' : value}`
    ),
    color: PARTNER_CATEGORY_COLORS[value] ?? '',
  }));

  const PARTNER_TYPES = PARTNER_TYPE_VALUES.map(value => ({
    value,
    label: t(`partner.typeLabels.${value}`),
  }));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { partner, isLoading, error, refetch } = usePartnerEditor(id);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    type: 'company',
    categories: [],
    name: '',
    shortName: '',
    taxNumber: '',
    registrationNumber: '',
    email: '',
    notificationEmail: '',
    billingEmail: '',
    billingEmailSame: true,
    phone: '',
    website: '',
    postalCode: '',
    city: '',
    street: '',
    building: '',
    temporaryAddress: '',
    tempAddressValidUntil: '',
    birthDate: '',
    birthPlace: '',
    mothersName: '',
    idCardNumber: '',
    idCardExpiry: '',
    lastDocumentScanDate: '',
    paymentTermDays: 8,
    creditLimit: '',
    discountPercent: '',
    discountRental: '',
    discountSales: '',
    discountService: '',
    rentalDepositPercent: '30',
    paymentBypassEnabled: false,
    idVerified: false,
    notes: '',
  });

  // Initialize form when partner loads
  useEffect(() => {
    if (partner) {
      setFormData(partnerToFormData(partner));
    }
  }, [partner]);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partner) return;

    if (!formData.name.trim()) {
      alert(t('partner.nameRequired'));
      return;
    }

    if (formData.categories.length === 0) {
      alert(t('partner.categoryRequired'));
      return;
    }

    setSaveError(null);
    setIsSaving(true);
    try {
      await updatePartnerEditor(partner.id, {
        // Q-T031: a BE nagybetűs enumot vár; a formData.type kisbetűs PartnerType
        // (a create-page :239 ugyanígy mappel — itt eddig nyersen ment → rossz case).
        type: formData.type === 'company' ? 'COMPANY' : 'INDIVIDUAL',
        name: formData.name,
        taxNumber: formData.taxNumber || undefined,
        email: formData.email || undefined,
        notificationEmail: formData.notificationEmail || null,
        billingEmail: formData.billingEmailSame
          ? formData.notificationEmail || null
          : formData.billingEmail || null,
        phone: formData.phone || undefined,
        postalCode: formData.postalCode || undefined,
        city: formData.city || undefined,
        address: formData.street || undefined,
        temporaryAddress: formData.temporaryAddress || undefined,
        tempAddressValidUntil: formData.tempAddressValidUntil || undefined,
        // Átvevő ID-adatok — conditional-omit: üres mezőt KIHAGYUNK (nem küldünk üres-stringet),
        // különben a BE #187 update-path (controller:681) a meglévő maszkolt értéket TÖRLI.
        ...(formData.birthDate.trim() ? { birthDate: formData.birthDate.trim() } : {}),
        ...(formData.birthPlace.trim() ? { birthPlace: formData.birthPlace.trim() } : {}),
        ...(formData.mothersName.trim() ? { mothersName: formData.mothersName.trim() } : {}),
        ...(formData.idCardNumber.trim() ? { idCardNumber: formData.idCardNumber.trim() } : {}),
        ...(formData.idCardExpiry.trim() ? { idCardExpiry: formData.idCardExpiry.trim() } : {}),
        idVerified: formData.idVerified,
        lastDocumentScanDate: formData.lastDocumentScanDate || undefined,
        paymentTermDays: formData.paymentTermDays,
        creditLimit: formData.creditLimit ? Number(formData.creditLimit) : undefined,
        // Q-T031: a generic kedvezmény a BE-n `defaultDiscountPc` (a #197 óta fogadott),
        // NEM `discountPercent` (az soha nem mappelődött → némán eldobódott).
        defaultDiscountPc: formData.discountPercent ? Number(formData.discountPercent) : undefined,
        discountRental: formData.discountRental ? Number(formData.discountRental) : undefined,
        discountSales: formData.discountSales ? Number(formData.discountSales) : undefined,
        discountService: formData.discountService ? Number(formData.discountService) : undefined,
        notes: formData.notes || undefined,
        // Q-T031 FE-törölve a payloadból (a BE update-handler NEM fogadja → némán eldobódott):
        // shortName / registrationNumber / website / rentalDepositPercent (phantom) /
        // paymentBypassEnabled (RBAC, dedikált approval-flow) / categories (type-inferált, read-only).
      });
      navigate(`/partners/${id}`);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t('partner.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 dark:text-[#ccfbf1] ml-3">{t('common.loading')}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <Card className="p-6 text-center">
          <p className="text-red-500 mb-4">
            {t('common.errorLabel')}: {error}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => refetch()}>{t('common.retry')}</Button>
            <Button variant="outline" onClick={() => navigate('/partners')}>
              {t('common.backToList')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!partner) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <Card className="p-6 text-center">
          <p className="text-gray-500 dark:text-[#ccfbf1] mb-4">{t('partner.partnerNotFound')}</p>
          <Button variant="outline" onClick={() => navigate('/partners')}>
            {t('common.backToList')}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(`/partners/${id}`)}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('partner.editTitle')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-[#99f6e4]">
                {partner.code} - {partner.name}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-primary hover:bg-primary/90"
          >
            {isSaving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type & Categories */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.typeAndCategory')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.partnerType')} *
                  </label>
                  <div className="flex gap-4">
                    {PARTNER_TYPES.map(type => (
                      <label key={type.value} className="flex cursor-pointer items-center gap-2">
                        <input
                          type="radio"
                          name="type"
                          value={type.value}
                          checked={formData.type === type.value}
                          onChange={e => updateField('type', e.target.value as PartnerType)}
                          className="h-4 w-4"
                        />
                        <span className="text-gray-700 dark:text-[#ccfbf1]">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.categories')}
                  </label>
                  {/* Q-T031: a kategóriák a BE-n a partner típusából inferálódnak (toDTO :811),
                      sosem tárolódnak külön → read-only megjelenítés, NEM szerkeszthető editor.
                      Valódi szerkeszthető kategóriák = külön epic (DEFER). */}
                  <div className="flex flex-wrap gap-2">
                    {PARTNER_CATEGORIES.filter(cat => formData.categories.includes(cat.value)).map(
                      cat => (
                        <span
                          key={cat.value}
                          className={`rounded-full px-3 py-1.5 text-sm font-medium ${cat.color}`}
                        >
                          {cat.label}
                        </span>
                      )
                    )}
                    {formData.categories.length === 0 && (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('partner.categoriesReadonlyHint')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.basicData')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {formData.type === 'company' ? t('partner.companyName') : t('partner.fullName')}{' '}
                    *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={e => updateField('name', e.target.value)}
                    placeholder={
                      formData.type === 'company'
                        ? t('partner.namePlaceholderCompany')
                        : t('partner.namePlaceholderIndividual')
                    }
                    required
                  />
                </div>

                {formData.type === 'company' && (
                  <>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                        {t('partner.shortName')}
                      </label>
                      <Input
                        value={formData.shortName}
                        readOnly
                        title={t('partner.fieldReadonlyHint')}
                        className="cursor-not-allowed bg-muted text-muted-foreground"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                        {t('partner.taxNumber')}
                      </label>
                      <Input
                        value={formData.taxNumber}
                        onChange={e => updateField('taxNumber', e.target.value)}
                        placeholder="12345678-2-42"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                        {t('partner.registrationNumber')}
                      </label>
                      <Input
                        value={formData.registrationNumber}
                        readOnly
                        title={t('partner.fieldReadonlyHint')}
                        className="cursor-not-allowed bg-muted text-muted-foreground"
                      />
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.contactInfo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">{t('partner.email')}</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={e => updateField('email', e.target.value)}
                    placeholder={t('partner.placeholder.email')}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.notificationEmail')}
                  </label>
                  <Input
                    type="email"
                    value={formData.notificationEmail}
                    onChange={e => updateField('notificationEmail', e.target.value)}
                    placeholder={t('partner.placeholder.email')}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    {t('partner.billingEmail')}
                  </label>
                  <Input
                    type="email"
                    value={
                      formData.billingEmailSame ? formData.notificationEmail : formData.billingEmail
                    }
                    onChange={e => updateField('billingEmail', e.target.value)}
                    placeholder={t('partner.placeholder.email')}
                    disabled={formData.billingEmailSame}
                  />
                  <label className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={formData.billingEmailSame}
                      onChange={e => updateField('billingEmailSame', e.target.checked)}
                      className="rounded border-input"
                    />
                    {t('partner.billingEmailSameAsNotification')}
                  </label>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">{t('partner.phone')}</label>
                  <Input
                    value={formData.phone}
                    onChange={e => updateField('phone', e.target.value)}
                    placeholder="+36 30 123 4567"
                  />
                </div>
                {formData.type === 'company' && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                      {t('partner.website')}
                    </label>
                    <Input
                      value={formData.website}
                      readOnly
                      title={t('partner.fieldReadonlyHint')}
                      className="cursor-not-allowed bg-muted text-muted-foreground"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Phone numbers (M2) */}
          {partner && (
            <Card>
              <CardHeader>
                <CardTitle>{t('partner.phoneNumbers')}</CardTitle>
              </CardHeader>
              <CardContent>
                <PhoneListEditor
                  partnerId={partner.id}
                  phones={(partner.phones ?? []).map(p => ({ ...p, label: p.label ?? '' }))}
                  onPhonesChange={refetch}
                />
              </CardContent>
            </Card>
          )}

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.billingAddress')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.postalCode')}
                  </label>
                  <Input
                    value={formData.postalCode}
                    onChange={e => updateField('postalCode', e.target.value)}
                    placeholder="1134"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.city')}
                  </label>
                  <Input
                    value={formData.city}
                    onChange={e => updateField('city', e.target.value)}
                    placeholder={t('partner.placeholder.city')}
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.streetAddress')}
                  </label>
                  <Input
                    value={formData.street}
                    onChange={e => updateField('street', e.target.value)}
                    placeholder={t('partner.streetPlaceholder')}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.floorDoor')}
                  </label>
                  <Input
                    value={formData.building}
                    onChange={e => updateField('building', e.target.value)}
                    placeholder="3. em."
                  />
                </div>
              </div>

              {/* Temporary address - collapsible (FG-1-4) */}
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  {t('partner.temporaryAddressSection')}
                </summary>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.temporaryAddress')}
                    </label>
                    <Input
                      value={formData.temporaryAddress}
                      onChange={e => updateField('temporaryAddress', e.target.value)}
                      placeholder={t('partner.placeholder.temporaryAddress')}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.tempAddressValidUntil')}
                    </label>
                    <Input
                      type="date"
                      value={formData.tempAddressValidUntil}
                      onChange={e => updateField('tempAddressValidUntil', e.target.value)}
                    />
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>

          {/* Personal data - only for individuals */}
          {formData.type === 'individual' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('partner.personalData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.birthDate')}
                    </label>
                    <Input
                      type="date"
                      value={formData.birthDate}
                      onChange={e => updateField('birthDate', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.birthPlace')}
                    </label>
                    <Input
                      value={formData.birthPlace}
                      onChange={e => updateField('birthPlace', e.target.value)}
                      placeholder={t('partner.placeholder.birthPlace')}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.mothersName')}
                    </label>
                    <Input
                      value={formData.mothersName}
                      onChange={e => updateField('mothersName', e.target.value)}
                      placeholder={t('partner.placeholder.mothersName')}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.idCardNumber')}
                    </label>
                    <Input
                      value={formData.idCardNumber}
                      onChange={e => updateField('idCardNumber', e.target.value)}
                      placeholder={t('partner.placeholder.idCardNumber')}
                      data-testid="partner-edit-id-number"
                    />
                    {/* #188 masked-ID hint (Javo GDPR-ratify): a mentett last-4 csak emlékeztető;
                        üresen-hagyva a save kihagyja → a BE megtartja a meglévő maszkoltat. */}
                    {partner.maskedIdCardNumber && (
                      <p
                        className="mt-1 text-xs text-muted-foreground"
                        data-testid="partner-edit-id-masked-hint"
                      >
                        {t('partner.idCardCurrentMasked', { masked: partner.maskedIdCardNumber })}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.idCardExpiry')}
                    </label>
                    <Input
                      type="date"
                      value={formData.idCardExpiry}
                      onChange={e => updateField('idCardExpiry', e.target.value)}
                      data-testid="partner-edit-id-expiry"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium">
                      {t('partner.lastDocumentScanDate')}
                    </label>
                    <Input
                      type="date"
                      value={formData.lastDocumentScanDate}
                      onChange={e => updateField('lastDocumentScanDate', e.target.value)}
                    />
                  </div>
                </div>
                {/* #196 (Q-T030) okmány-verify — manuális staff-verify; a BE false→true-n
                    stampeli idVerifiedAt/By-t. A verify-badge a GET idVerifiedAt-jából. */}
                <div className="mt-4 border-t pt-4 dark:border-[#2d7d8a]">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      data-testid="partner-edit-id-verified"
                      checked={formData.idVerified}
                      onChange={e => updateField('idVerified', e.target.checked)}
                      className="h-4 w-4 rounded border-input"
                    />
                    <span className="text-sm font-medium">{t('partner.idVerified')}</span>
                  </label>
                  {partner.idVerified && (
                    <p
                      data-testid="partner-edit-id-verified-badge"
                      className="mt-1 ml-7 text-xs font-medium text-green-600 dark:text-green-400"
                    >
                      {t('partner.idVerified')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Financial */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partner.financialSettings')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.paymentTermDays')}
                  </label>
                  <Input
                    type="number"
                    value={formData.paymentTermDays}
                    onChange={e => updateField('paymentTermDays', Number(e.target.value))}
                    min={0}
                    max={90}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.creditLimitFt')}
                  </label>
                  <Input
                    type="number"
                    value={formData.creditLimit}
                    onChange={e => updateField('creditLimit', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('partner.discountPercent')}
                  </label>
                  <Input
                    type="number"
                    value={formData.discountPercent}
                    onChange={e => updateField('discountPercent', e.target.value)}
                    placeholder="0"
                    min={0}
                    max={50}
                  />
                </div>
              </div>

              {/* Kedvezmény kategorizálás (O-PRT-1) */}
              <div className="mt-6 border-t pt-4 dark:border-[#2d7d8a]">
                <h4 className="mb-1 text-sm font-semibold text-gray-700 dark:text-[#ccfbf1]">
                  {t('partner.discount.categorized')}
                </h4>
                <p className="mb-3 text-xs text-gray-500 dark:text-[#99f6e4]">
                  {t('partner.discount.max')} &mdash; {t('partner.discount.noStacking')}
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                      {t('partner.discount.rental')}
                    </label>
                    <Input
                      type="number"
                      value={formData.discountRental}
                      onChange={e => updateField('discountRental', e.target.value)}
                      placeholder="0"
                      min={0}
                      max={15}
                      step={1}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                      {t('partner.discount.sales')}
                    </label>
                    <Input
                      type="number"
                      value={formData.discountSales}
                      onChange={e => updateField('discountSales', e.target.value)}
                      placeholder="0"
                      min={0}
                      max={15}
                      step={1}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                      {t('partner.discount.service')}
                    </label>
                    <Input
                      type="number"
                      value={formData.discountService}
                      onChange={e => updateField('discountService', e.target.value)}
                      placeholder="0"
                      min={0}
                      max={15}
                      step={1}
                    />
                  </div>
                </div>
              </div>

              {/* Q-T031: rentalDepositPercent PHANTOM (nincs ilyen model-mező; depositExemption
                  enum van) → UI-blokk eltávolítva, a payloadból se megy. */}

              {/* FG-3-3: Vezetői bypass — RBAC-gated, dedikált bypassApprovedBy/At approval-flow.
                  Q-T031: a sima partner-edit NEM kapcsolhatja (gate-bypass vektor lenne) →
                  read-only állapot-megjelenítés, a generic payloadból kivéve. */}
              <div className="mt-4 border-t pt-4 dark:border-[#2d7d8a]">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.paymentBypassEnabled}
                    disabled
                    readOnly
                    className="h-4 w-4 rounded border-input opacity-60"
                  />
                  <span className="text-sm font-medium text-muted-foreground">
                    {t('partner.paymentBypassEnabled')}
                  </span>
                </label>
                <p className="mt-1 ml-7 text-xs text-muted-foreground">
                  {t('partner.paymentBypassReadonlyHint')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('common.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white p-3 text-gray-900 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
                rows={4}
                placeholder={t('partner.notesPlaceholder')}
              />
            </CardContent>
          </Card>

          {/* Error display */}
          {saveError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/30">
              <p className="text-red-800 dark:text-red-200">{saveError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate(`/partners/${id}`)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSaving} className="bg-primary hover:bg-primary/90">
              {isSaving ? t('common.saving') : t('partner.saveChanges')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
